import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { typeLabel } from "@/utils/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const goPrev = () =>
    setActive((prev) => (prev - 1 + items.length) % items.length);
  const goNext = () => setActive((prev) => (prev + 1) % items.length);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[440px] w-full md:h-[560px]">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.backdrop}
              alt={`${item.title}焦点图`}
              title={`${item.title} - 影视详情`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full max-w-page flex-col justify-center px-4 md:px-6">
          <div key={items[active].id} className="max-w-xl animate-fadeInUp">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-accent-950">
              <i className="ri-fire-fill" aria-hidden="true" />
              本周焦点
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {items[active].title}
            </h2>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100">
              <span className="flex items-center gap-1 font-bold text-accent-400">
                <i className="ri-star-fill" aria-hidden="true" />
                {items[active].rating.toFixed(1)}
              </span>
              <span>{typeLabel[items[active].type]}</span>
              <span>{items[active].year}</span>
              <span>{items[active].region}</span>
              <span>{items[active].duration}</span>
            </div>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
              {items[active].synopsis}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${items[active].id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-600"
              >
                <i className="ri-information-line text-lg" aria-hidden="true" />
                查看详情
              </Link>
              <Link
                to={`/category/${items[active].type}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
              >
                <i className="ri-layout-grid-line text-lg" aria-hidden="true" />
                更多同类
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={goPrev}
          aria-label="上一张"
          className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/30 md:flex md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={goNext}
          aria-label="下一张"
          className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/30 md:flex md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => setActive(index)}
              className={`h-2 cursor-pointer rounded-full transition-all ${
                index === active
                  ? "w-7 bg-accent-500"
                  : "w-2 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}